import { getSessionAuthentication, baseUrl, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Vat {
    id: number;
    vatCode: string;
    vatName: string;
    vatRate: number;
}

export interface CountryVat {
    vat: Vat;
    vatRate: number;
}

export interface Country {
    id: number;
    code: string;
    name: string;
    vats: CountryVat[];
}

export async function getCountry(id: number): Promise<Country> {
    return legacyFetch<Country>(`countries/${id}`);
}

export async function createCountry(c: Partial<Country>): Promise<Country> {
    return legacyFetch<Country>('countries', {
        method: 'POST',
        body: JSON.stringify(c),
    });
}

export async function updateCountry(c: Country): Promise<Country> {
    return legacyFetch<Country>(`countries`, {
        method: 'PUT',
        body: JSON.stringify(c),
    });
}

export async function deleteCountry(id: number): Promise<void> {
    await legacyFetch<void>(`countries/${id}`, { method: 'DELETE' });
}

export async function getVats(): Promise<Vat[]> {
    const response = await rms2Fetch<{ vats: Vat[] }>('vats');
    return response?.vats ?? [];
}
